<template>
  <div class="card border-0 shadow-sm">
    <div class="card-header pb-0">
      <h6 class="mb-0">Seguimiento de Inventario</h6>
    </div>
    <div class="card-body">
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
      </div>
      <div v-else-if="data">
        <div class="row g-3 mb-3">
          <div class="col-6 col-md-3">
            <div class="text-center">
              <p class="text-xs text-secondary mb-0">Total Items</p>
              <h5 class="font-weight-bolder mb-0">{{ data.summary.total_items }}</h5>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="text-center">
              <p class="text-xs text-secondary mb-0">Valor Inventario</p>
              <h5 class="font-weight-bolder mb-0">{{ formatMoney(data.summary.total_value) }}</h5>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="text-center">
              <p class="text-xs text-secondary mb-0">Stock Bajo</p>
              <h5 class="font-weight-bolder mb-0 text-warning">{{ data.summary.low_stock_count }}</h5>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="text-center">
              <p class="text-xs text-secondary mb-0">Agotados</p>
              <h5 class="font-weight-bolder mb-0 text-danger">{{ data.summary.out_of_stock_count }}</h5>
            </div>
          </div>
        </div>

        <div class="row g-4">
          <div class="col-12 col-lg-6">
            <h6 class="text-sm font-weight-bold mb-2">Más Vendidos</h6>
            <div class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Producto</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Vendido</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Ingresos</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in data.top_sold" :key="item.production_id">
                    <td class="text-sm font-weight-bold">{{ item.name }}</td>
                    <td class="text-center text-sm">{{ item.total_sold }}</td>
                    <td class="text-center text-sm text-success">{{ formatMoney(item.total_revenue) }}</td>
                  </tr>
                  <tr v-if="data.top_sold.length === 0">
                    <td colspan="3" class="text-center text-sm text-secondary py-3">Sin datos</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="col-12 col-lg-6">
            <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
              <h6 class="text-sm font-weight-bold mb-0">Stock Bajo / Agotado</h6>
              <input
                v-model="search"
                type="text"
                class="form-control form-control-sm"
                style="max-width: 180px;"
                placeholder="Buscar producto..."
                @input="onSearch"
              />
            </div>
            <div class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Producto</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Ref</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Cantidad</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in data.stock_alerts" :key="item.alert + '-' + item.id">
                    <td class="text-sm font-weight-bold">{{ item.name }}</td>
                    <td class="text-center text-sm">{{ item.ref || '—' }}</td>
                    <td class="text-center text-sm" :class="item.alert === 'out' ? 'text-danger font-weight-bold' : 'text-warning font-weight-bold'">{{ item.quantity }}</td>
                    <td class="text-center">
                      <span class="badge" :class="item.alert === 'out' ? 'bg-danger' : 'bg-warning'">
                        {{ item.alert === 'out' ? 'Agotado' : 'Stock Bajo' }}
                      </span>
                    </td>
                  </tr>
                  <tr v-if="data.stock_alerts.length === 0">
                    <td colspan="4" class="text-center text-sm text-secondary py-3">Todo el inventario está bien</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="d-flex align-items-center justify-content-between pt-2" v-if="data.pagination">
              <span class="text-xs text-secondary">
                {{ data.pagination.total }} registros · pág. {{ data.pagination.page }}/{{ data.pagination.last_page }}
              </span>
              <div class="btn-group btn-group-sm">
                <button class="btn btn-outline-primary mb-0" :disabled="data.pagination.page <= 1" @click="changePage(data.pagination.page - 1)">Anterior</button>
                <button class="btn btn-outline-primary mb-0" :disabled="data.pagination.page >= data.pagination.last_page" @click="changePage(data.pagination.page + 1)">Siguiente</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InventoryTracking',
  data() {
    return {
      loading: false,
      data: null,
      search: '',
      page: 1,
      searchTimer: null,
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const response = await this.$store.dispatch('metrics/getInventoryTracking', {
          top_limit: 10,
          search: this.search,
          page: this.page,
        });
        this.data = response.data;
      } catch (error) {
        console.error('Error loading inventory tracking:', error);
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.loadData();
      }, 400);
    },
    changePage(page) {
      this.page = page;
      this.loadData();
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
  },
};
</script>
